import { GpxImportErrorCode, isMalformedXml } from '../../utils/helpers/gpxImportError';
import { decodeGpxName } from '../../utils/helpers/decodeGpxText';

/** One point as the importer uses it, after the parser's own shape is normalised. */
export interface GpxTrackPoint {
  lat: number;
  lon: number;
  elevation: number;
  time: unknown;
}

/** The subset of a `gpx-parser-builder` point this module reads. */
interface ParsedPoint {
  lat: number;
  lon: number;
  ele?: number | null;
  time?: unknown;
}

/**
 * Why a GPX file cannot be imported as a recording, decided in one place.
 *
 * These checks used to sit inline in ImportRecordingDialog's `parseGpxFile`,
 * where the only way to exercise them was to mount the component and feed a
 * FileReader. They are pure decisions over parsed data, so they live here and
 * the component reads the answer — which is the shape CLAUDE.md's Test Policy
 * asks for, and the reason the timestamp rule below can be pinned at all.
 */

/**
 * The text-level checks, before the parser runs.
 *
 * `isMalformedXml` is separate from "empty" because DOMParser does not throw on
 * damaged XML — it injects a <parsererror> node, so a broken file parses to zero
 * features and is indistinguishable from an empty one unless it is asked.
 */
export function gpxTextImportError(text: string | null | undefined): GpxImportErrorCode | null {
  if (!text || text.trim() === '') return GpxImportErrorCode.Empty;
  if (isMalformedXml(text)) return GpxImportErrorCode.MalformedXml;

  return null;
}

/**
 * The track-level checks, after it has parsed.
 *
 * The timestamp rule is the recording/route distinction: a planner export has
 * coordinates but no per-point time. Rejecting it here, rather than importing a
 * timeless "recording", is what keeps peak detection honest — its dwell gate has
 * nothing to work with otherwise, and every peak on the route would either all
 * bag or none would.
 *
 * "Some point has a time" rather than "every point does": real GPS tracks drop
 * the odd sample, and a file that is 99% timed is a recording by any reading.
 */
export function gpxTrackImportError(points: GpxTrackPoint[] | null | undefined): GpxImportErrorCode | null {
  if (!points || points.length === 0) return GpxImportErrorCode.NoRouteOrTrack;
  if (!points.some((point) => point.time)) return GpxImportErrorCode.NoTimestamps;

  return null;
}

/** Whether the parser found anything to import at all. */
export function hasTracks(tracks: unknown[] | null | undefined): boolean {
  return Array.isArray(tracks) && tracks.length > 0;
}

/**
 * Normalises the parser's points to the importer's shape.
 *
 * A missing elevation becomes 0 rather than undefined because the server's
 * `altitudes` array is positional — a hole in it would shift every later point's
 * height onto the wrong coordinate.
 */
export function toTrackPoints(points: ParsedPoint[] | null | undefined): GpxTrackPoint[] {
  if (!points) return [];

  return points.map((point) => ({
    lat: point.lat,
    lon: point.lon,
    elevation: point.ele || 0,
    time: point.time,
  }));
}

/**
 * The recording's title: the file's <metadata><name> when it has one, else the
 * caller's fallback (which the dialog derives from the filename).
 *
 * The decode is the point. gpxparser reads text elements with `elem.innerHTML`,
 * which serialises the node rather than returning its text, so a name the file
 * wrapped or escaped arrives still wrapped or still escaped. AllTrails wraps
 * its names in CDATA, and one of its exports was imported and listed as
 * "<![CDATA[Afternoon hike at Warrenscourt: Wood Link and Kilmurry Wood Loop]]>".
 *
 * Lives here rather than in the component because ImportRecordingDialog.vue
 * cannot be loaded under Jest at all — gpxparser's CommonJS entry fights the
 * test environment — so a decision left inline there cannot be tested.
 */
export function recordingTitleFrom(
  parsed: { metadata?: { name?: unknown } } | null | undefined,
  fallback: string
): string {
  const metadataName = decodeGpxName(parsed?.metadata?.name);

  return metadataName && metadataName.trim() !== '' ? metadataName : fallback;
}
